import { Tooltip } from '@fluentui/react-components';
import classNames from 'classnames';
import { ReactNode, useContext } from 'react';

import DocumentContext from '../contexts/DocumentContext.context';

import useDelayedIndicator from '../hooks/useDelayedIndicator.hook';
import useEditorActive from '../hooks/useEditorActive.hook';
import { isAiEditReviewActive } from '../tiptap/plugins/AiEditReviewPlugin';

export const AI_EDIT_REVIEW_LOCK_REASON = 'Document controls are unavailable while reviewing AI changes.';
export const EXPLICIT_SAVE_LOCK_REASON = 'Saving document. Please wait.';

type TProps = {
  children: ReactNode;
  className?: string;
  disabledReason?: string;
};

/** Keeps temporarily unavailable document controls visible, inert, and explained. */
const DocumentControlsLock = ({ children, className, disabledReason }: TProps) => {
  const isAiEditLocked = useEditorActive((editor) => isAiEditReviewActive(editor.state), false);
  const isExplicitSavePending = Boolean(useContext(DocumentContext)?.isExplicitSavePending);
  const showSavingCover = useDelayedIndicator(isExplicitSavePending);
  const reason = showSavingCover
    ? EXPLICIT_SAVE_LOCK_REASON
    : isAiEditLocked
      ? AI_EDIT_REVIEW_LOCK_REASON
      : disabledReason;
  const isLocked = isExplicitSavePending || Boolean(reason);

  return (
    <div className={classNames('relative', className)}>
      <div
        className={classNames('contents', isLocked && '[&_*]:!cursor-not-allowed')}
        aria-disabled={isLocked ? true : undefined}
        {...(isLocked ? { inert: '' } : {})}
      >
        {children}
      </div>
      {reason && (
        <Tooltip content={reason} withArrow relationship="description">
          <div
            role="note"
            aria-label={reason}
            className="absolute inset-0 z-50 cursor-not-allowed rounded-lg bg-white opacity-45"
          />
        </Tooltip>
      )}
    </div>
  );
};

export default DocumentControlsLock;
